import type { Actor } from "./requestPresentation.js";

export type AuditEvent = {
  id: string;
  environment: "sandbox" | "production";
  event_type: string;
  created_at: string;
  data: unknown;
  request_id: string | null;
  permit_id: string | null;
  actor: Actor;
};

export type AuditCategory = "all" | "authorization" | "policy" | "access" | "security" | "other";

export const auditCategoryLabel: Record<Exclude<AuditCategory, "all">, string> = {
  authorization: "Authorization",
  policy: "Policy & packs",
  access: "Workspace & keys",
  security: "Security",
  other: "Other"
};

export const categorizeAuditEvent = (eventType: string): Exclude<AuditCategory, "all"> => {
  if (eventType.startsWith("policy.") || eventType.startsWith("policy_pack.")) {
    return "policy";
  }
  if (
    eventType.startsWith("member.") ||
    eventType.startsWith("workspace.") ||
    eventType.startsWith("api_key.")
  ) {
    return "access";
  }
  if (eventType.startsWith("antibypass.") || eventType === "request.schema_invalid") {
    return "security";
  }
  if (
    eventType.startsWith("request.") ||
    eventType.startsWith("approval.") ||
    eventType.startsWith("permit.") ||
    eventType.startsWith("gateway.")
  ) {
    return "authorization";
  }
  return "other";
};

const eventTitleMap: Record<string, string> = {
  "request.received": "Request received",
  "request.expired": "Request expired",
  "request.schema_invalid": "Invalid request schema",
  "policy.evaluated": "Policy evaluated",
  "policy.updated": "Policy updated",
  "policy_pack.installed": "Policy pack installed",
  "approval.requested": "Approval requested",
  "approval.approved": "Approval granted",
  "approval.rejected": "Approval rejected",
  "permit.issued": "Permit issued",
  "gateway.executed": "Gateway execution",
  "gateway.denied": "Gateway denied",
  "api_key.created": "API key created",
  "api_key.revoked": "API key revoked",
  "member.invited": "Member invited",
  "member.role_changed": "Member role changed",
  "workspace.strict_separation_updated": "Production separation updated",
  "workspace.access_migration_applied": "Access migration applied",
  "antibypass.agent_denied": "Agent bypass denied",
  "antibypass.tool_manifest_drift": "Tool manifest drift"
};

export const formatAuditEventTitle = (eventType: string) => {
  if (eventTitleMap[eventType] != null) {
    return eventTitleMap[eventType];
  }
  return eventType
    .split(/[._]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const auditEventBadgeVariant = (
  eventType: string
): "success" | "destructive" | "warning" | "default" | "secondary" => {
  if (
    eventType.includes("allowed") ||
    eventType.includes("approved") ||
    eventType.includes("issued") ||
    eventType.includes("executed")
  ) {
    return "success";
  }
  if (
    eventType.includes("denied") ||
    eventType.includes("rejected") ||
    eventType.includes("expired") ||
    eventType.includes("revoked") ||
    eventType.includes("invalid")
  ) {
    return "destructive";
  }
  if (eventType.includes("pending") || eventType.includes("requested")) {
    return "warning";
  }
  if (eventType.startsWith("policy")) {
    return "default";
  }
  if (eventType.startsWith("member") || eventType.startsWith("workspace")) {
    return "secondary";
  }
  return "secondary";
};

export const auditCategoryAccent: Record<Exclude<AuditCategory, "all">, string> = {
  authorization: "#7950f2",
  policy: "#2563eb",
  access: "#0d9488",
  security: "#dc2626",
  other: "#6b7280"
};

export const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });

export const formatRelativeTime = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) {
    return iso;
  }
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${String(minutes)}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    return `${String(hours)}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 14) {
    return `${String(days)}d ago`;
  }
  return formatDateTime(iso);
};

export const eventMatchesSearch = (event: AuditEvent, term: string) => {
  const t = term.trim().toLowerCase();
  if (t.length === 0) {
    return true;
  }
  const haystack = [
    event.id,
    event.event_type,
    event.request_id ?? "",
    event.permit_id ?? "",
    event.actor.label,
    event.actor.type === "user" ? (event.actor.email ?? "") : "",
    event.actor.type === "user" ? (event.actor.name ?? "") : "",
    JSON.stringify(event.data ?? {})
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(t);
};
